import React, { useEffect, useRef, useState } from 'react';
import { ARTICLES as FALLBACK_ARTICLES } from '../data/articles';
import { ArrowLeft, ArrowRight, Clock, Calendar, Share2, Tag, Twitter, Linkedin, Link as LinkIcon, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useGoToContact } from '../hooks/useGoToContact';
import ReadingProgressBar from './ReadingProgressBar';

// Helper: set or create a meta tag in <head>. Returns previous content for restoration.
const setMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  const existed = !!el;
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  const previous = el.getAttribute('content');
  el.setAttribute('content', attrs.content);
  return { el, previous, existed };
};

const useArticleSeo = (article) => {
  useEffect(() => {
    if (!article) return;
    const previousTitle = document.title;
    document.title = `${article.title} — Grouth Insights`;

    const fullImage = article.image.startsWith('http')
      ? article.image
      : `${window.location.origin}${article.image}`;
    const url = window.location.href;
    const description = article.metaDescription || article.excerpt;

    const tags = [
      // Standard description
      [`meta[name="description"]`, { create: { name: 'description' }, content: description }],
      // OpenGraph
      [`meta[property="og:title"]`, { create: { property: 'og:title' }, content: article.title }],
      [`meta[property="og:description"]`, { create: { property: 'og:description' }, content: description }],
      [`meta[property="og:image"]`, { create: { property: 'og:image' }, content: fullImage }],
      [`meta[property="og:url"]`, { create: { property: 'og:url' }, content: url }],
      [`meta[property="og:type"]`, { create: { property: 'og:type' }, content: 'article' }],
      [`meta[property="og:site_name"]`, { create: { property: 'og:site_name' }, content: 'Grouth' }],
      // Twitter
      [`meta[name="twitter:card"]`, { create: { name: 'twitter:card' }, content: 'summary_large_image' }],
      [`meta[name="twitter:title"]`, { create: { name: 'twitter:title' }, content: article.title }],
      [`meta[name="twitter:description"]`, { create: { name: 'twitter:description' }, content: description }],
      [`meta[name="twitter:image"]`, { create: { name: 'twitter:image' }, content: fullImage }],
    ];

    const restorers = tags.map(([selector, attrs]) => setMeta(selector, attrs));

    // JSON-LD structured data (Article schema) — helps Google rich results
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'article-jsonld';
    const ldBody = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description,
      image: [fullImage],
      author: { '@type': 'Organization', name: article.author || 'Grouth' },
      publisher: {
        '@type': 'Organization',
        name: 'Grouth',
        logo: { '@type': 'ImageObject', url: `${window.location.origin}/grouth-logo.png` },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      datePublished: article.publishedAt || article.date,
      keywords: (article.tags || []).join(', '),
      articleSection: article.category,
    };
    ld.text = JSON.stringify(ldBody);
    document.head.appendChild(ld);

    return () => {
      document.title = previousTitle;
      // Restore previous meta values where they existed; remove the ones we created.
      restorers.forEach(({ el, previous, existed }) => {
        if (!existed) {
          el.parentNode && el.parentNode.removeChild(el);
        } else if (previous !== null) {
          el.setAttribute('content', previous);
        }
      });
      ld.parentNode && ld.parentNode.removeChild(ld);
    };
  }, [article]);
};

const ShareButtons = ({ article }) => {
  const [copied, setCopied] = React.useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `${article.title} — Grouth`;

  const onCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      });
    }
  };

  const onTwitter = () => {
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
    window.open(u, '_blank', 'noopener,noreferrer,width=620,height=480');
  };

  const onLinkedin = () => {
    const u = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(u, '_blank', 'noopener,noreferrer,width=720,height=560');
  };

  const onNative = () => {
    if (navigator.share) {
      navigator.share({ title: article.title, url }).catch(() => {});
    } else {
      onCopy();
    }
  };

  return (
    <div className="flex items-center gap-1.5" data-testid="article-share-row">
      <span className="text-[#B8B8B8] text-xs font-['Inter'] uppercase tracking-wider mr-1 hidden sm:inline">
        Share
      </span>
      <button
        onClick={onTwitter}
        aria-label="Share on X / Twitter"
        data-testid="article-share-twitter"
        className="w-9 h-9 inline-flex items-center justify-center rounded-full text-[#4A4A48] hover:bg-[#83BF42]/10 hover:text-[#83BF42] transition-colors"
      >
        <Twitter className="w-4 h-4" />
      </button>
      <button
        onClick={onLinkedin}
        aria-label="Share on LinkedIn"
        data-testid="article-share-linkedin"
        className="w-9 h-9 inline-flex items-center justify-center rounded-full text-[#4A4A48] hover:bg-[#83BF42]/10 hover:text-[#83BF42] transition-colors"
      >
        <Linkedin className="w-4 h-4" />
      </button>
      <button
        onClick={onCopy}
        aria-label="Copy link"
        data-testid="article-share-copy"
        className="w-9 h-9 inline-flex items-center justify-center rounded-full text-[#4A4A48] hover:bg-[#83BF42]/10 hover:text-[#83BF42] transition-colors"
      >
        {copied ? <Check className="w-4 h-4 text-[#83BF42]" /> : <LinkIcon className="w-4 h-4" />}
      </button>
      <button
        onClick={onNative}
        aria-label="More share options"
        data-testid="article-share-button"
        className="w-9 h-9 inline-flex items-center justify-center rounded-full text-[#4A4A48] hover:bg-[#83BF42]/10 hover:text-[#83BF42] transition-colors lg:hidden"
      >
        <Share2 className="w-4 h-4" />
      </button>
    </div>
  );
};

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const goToContact = useGoToContact();

  // Initial state from local fallback so the page renders instantly; replaced by API data.
  const [articles, setArticles] = useState(FALLBACK_ARTICLES);
  const [related, setRelated] = useState([]);
  const article = articles.find(a => a.slug === slug);
  const articleRef = useRef(null);

  useEffect(() => {
    api.getArticles().then(data => {
      if (Array.isArray(data) && data.length > 0) setArticles(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!slug) return;
    api.getRelatedArticles(slug, 3)
      .then((data) => setRelated(Array.isArray(data) ? data : []))
      .catch(() => setRelated([]));
  }, [slug]);

  // Fetch the live article (with view counter) so views show fresh and SEO data is canonical
  useEffect(() => {
    if (!slug) return;
    api.getArticle(slug).then((live) => {
      if (!live) return;
      setArticles((prev) => {
        const idx = prev.findIndex((a) => a.slug === slug);
        if (idx === -1) return [...prev, live];
        const out = [...prev];
        out[idx] = { ...prev[idx], ...live };
        return out;
      });
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useArticleSeo(article);

  const currentIndex = articles.findIndex(a => a.slug === slug);
  const nextArticle = articles[currentIndex + 1] || articles[0];
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : articles[articles.length - 1];

  if (!article) {
    return (
      <div className="bg-[#FDFEFA] min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-[#242326] text-3xl font-['Inter'] mb-4">Article not found</h1>
          <button onClick={() => navigate('/insights')} className="text-[#83BF42] font-['Inter']">
            Back to Insights
          </button>
        </div>
      </div>
    );
  }

  const paragraphs = article.body.split('\n\n').filter(p => p.trim());

  return (
    <div className="bg-[#FDFEFA] min-h-screen" data-testid="article-page" ref={articleRef}>
      <ReadingProgressBar targetRef={articleRef} />
      {/* Hero */}
      <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#242326] via-[#242326]/60 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 pb-10 md:pb-16">
          <div className="max-w-[900px] mx-auto px-6 md:px-12">
            <button
              onClick={() => navigate('/insights')}
              data-testid="article-back-insights"
              className="inline-flex items-center gap-2 text-white/50 text-sm font-['Inter'] mb-6 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              All Insights
            </button>

            <div className="flex items-center gap-3 mb-4">
              <span className="text-[#83BF42] text-xs uppercase tracking-wider font-bold font-['Inter']">
                {article.category}
              </span>
              <span className="text-white/40 text-xs">|</span>
              <span className="text-white/60 text-xs font-['Lato'] flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {article.date}
              </span>
              <span className="text-white/60 text-xs font-['Lato'] flex items-center gap-1">
                <Clock className="w-3 h-3" /> {article.readTime}
              </span>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white text-3xl md:text-5xl lg:text-[56px] font-light leading-[1.1] tracking-tight font-['Inter']"
            >
              {article.title}
            </motion.h1>
          </div>
        </div>
      </div>

      {/* Article Body */}
      <div className="max-w-[720px] mx-auto px-6 md:px-12 py-12 md:py-16">
        {/* Lead / Excerpt */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[#242326] text-xl md:text-2xl font-['Inter'] font-light leading-relaxed mb-10 pb-10 border-b border-[#242326]/8"
        >
          {article.excerpt}
        </motion.p>

        {/* Body Paragraphs */}
        <div className="space-y-6">
          {paragraphs.map((para, index) => (
            <motion.p
              key={index}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-30px' }}
              transition={{ duration: 0.4 }}
              className="text-[#4A4A48] text-[17px] leading-[1.8] font-['Lato'] font-light"
            >
              {para}
            </motion.p>
          ))}
        </div>

        {/* Tags */}
        {Array.isArray(article.tags) && article.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-[#242326]/8 flex flex-wrap items-center gap-2" data-testid="article-tags">
            <Tag className="w-3.5 h-3.5 text-[#B8B8B8] mr-1" />
            {article.tags.map((t) => (
              <button
                key={t}
                onClick={() => navigate(`/insights?tag=${encodeURIComponent(t)}`)}
                data-testid={`article-tag-${t.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-3 py-1 rounded-full text-xs font-['Inter'] bg-[#242326]/5 text-[#4A4A48] hover:bg-[#83BF42]/15 hover:text-[#83BF42] transition-colors"
              >
                #{t}
              </button>
            ))}
          </div>
        )}

        {/* Author / Share */}
        <div className="mt-12 pt-8 border-t border-[#242326]/8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#83BF42]/10 flex items-center justify-center">
              <span className="text-[#83BF42] font-bold text-sm font-['Inter']">G</span>
            </div>
            <div>
              <p className="text-[#242326] text-sm font-medium font-['Inter']">{article.author}</p>
              <p className="text-[#B8B8B8] text-xs font-['Lato']">{article.date}</p>
            </div>
          </div>
          <ShareButtons article={article} />
        </div>

        {/* Get in Touch CTA */}
        <div className="mt-12 bg-[#242326] rounded-2xl p-8 md:p-10">
          <h3 className="text-white text-xl md:text-2xl font-medium font-['Inter'] mb-2">
            Need help with your growth strategy?
          </h3>
          <p className="text-white/50 text-sm font-['Lato'] font-light mb-6">
            We work with ambitious brands to turn these ideas into results.
          </p>
          <button
            onClick={goToContact}
            data-testid="article-cta-contact"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-[#83BF42] text-[#83BF42] text-[15px] font-['Inter'] font-light hover:bg-[#83BF42] hover:text-white transition-all duration-300 group"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Related Articles */}
      {related.length > 0 && (
        <div className="border-t border-[#242326]/8 bg-white" data-testid="article-related">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-12 md:py-16">
            <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-bold font-['Inter'] mb-2">
              Read next
            </p>
            <h3 className="text-[#242326] text-2xl md:text-3xl font-light font-['Inter'] mb-8">
              Related insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {related.map((r, i) => (
                <motion.button
                  key={r.id || r.slug}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  onClick={() => navigate(`/insights/${r.slug}`)}
                  data-testid={`article-related-${r.slug}`}
                  className="text-left group"
                >
                  <div className="relative h-[170px] rounded-xl overflow-hidden mb-4">
                    <img
                      src={r.image}
                      alt={r.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-white/90 backdrop-blur-sm text-[#242326] text-[10px] font-medium font-['Inter'] px-2.5 py-1 rounded-full uppercase tracking-wider">
                        {r.category}
                      </span>
                    </div>
                  </div>
                  <h4 className="text-[#242326] text-base md:text-lg font-medium font-['Inter'] leading-snug group-hover:text-[#83BF42] transition-colors">
                    {r.title}
                  </h4>
                  <p className="text-[#4A4A48] text-sm font-['Lato'] font-light leading-relaxed mt-2 line-clamp-2">
                    {r.excerpt}
                  </p>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Next / Previous Navigation */}
      <div className="border-t border-[#242326]/8">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <button
              onClick={() => navigate(`/insights/${prevArticle.slug}`)}
              data-testid="article-prev-link"
              className="group text-left"
            >
              <span className="text-[#B8B8B8] text-xs uppercase tracking-wider font-['Inter'] flex items-center gap-1 mb-2">
                <ArrowLeft className="w-3 h-3" /> Previous
              </span>
              <h4 className="text-[#242326] text-lg font-medium font-['Inter'] group-hover:text-[#83BF42] transition-colors leading-snug">
                {prevArticle.title}
              </h4>
            </button>
            <button
              onClick={() => navigate(`/insights/${nextArticle.slug}`)}
              data-testid="article-next-link"
              className="group text-left md:text-right"
            >
              <span className="text-[#B8B8B8] text-xs uppercase tracking-wider font-['Inter'] flex items-center gap-1 mb-2 md:justify-end">
                Next <ArrowRight className="w-3 h-3" />
              </span>
              <h4 className="text-[#242326] text-lg font-medium font-['Inter'] group-hover:text-[#83BF42] transition-colors leading-snug">
                {nextArticle.title}
              </h4>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;
